import React, { useContext } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { createTheme, ThemeProvider, Typography } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useHistory } from 'react-router-dom';
import { reactLocalStorage } from 'reactjs-localstorage';
import { LoggedInUserContext } from '../context/LoggedInUserContext';
import * as Constants from './api/constants';
import Copyright from './copyright.component/copyright';

import logo from './resources/spanet_logo.png';
import bottomImage from './resources/spanet_bottom_wave.png';
import backgroundImage from './resources/background_orange_blue_gradient.png';
import btnBackground from './resources/spanet_button_background.png';

const theme = createTheme();

export default function SignInSide() {
  const history = useHistory();
  const state = useContext(LoggedInUserContext);

  const [userIdPlaceholder, setUserIdPlaceholder] = React.useState("User ID");
  const [pinPlaceholder, setPinPlaceholder] = React.useState("PIN");

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const requestOptions = {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        UserId: data.get('userId'),
        Pin: data.get('PIN'),
        UserDeviceId: 'web',
      }),
      credentials: "include"
    };

    fetch(Constants.baseURL + Constants.apiLoginPin, requestOptions)
      .then((response) => {
        if (response.ok) {
          reactLocalStorage.set('isLoggedIn', true);
          history.push('/Dashboard');
        } else if (response.status === 401 || response.status === 400) {
          toast.error('Incorrect user ID or PIN. Please try again');
        } else if (response.status === 204) {
          toast.error('User not active. Please try again');
        } else if (response.status === 403) {
          return response.json();
        }
      })
      .then((data) => {
        if (data !== undefined) {
          toast.error(
            `The account is locked due to 5 failed logins. Please try again in ${data} minutes`
          );
        }
      })
      .catch((error) => console.log('request failed', error));
  };

  return (
    <ThemeProvider theme={theme}>
      <ToastContainer />
      <CssBaseline />
      <Box
        sx={{
          height: "100vh",
          backgroundImage: `url(${backgroundImage})`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            padding: 4,
            width: 360,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "transparent",
          }}
        >
          <Box mb={1}>
            <img src={logo} alt="SpaNET Logo" style={{ width: 250 }} />
          </Box>
          <Typography
            variant="h8"
            sx={{
              color: "white",
              mt: 0,
              mb: 3,
              fontWeight: "regular",
              textAlign: "center",
            }}
          >
            Remote Login
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "90%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="userId"
              name="userId"
              autoComplete="userId"
              autoFocus
              placeholder={userIdPlaceholder}
              onFocus={() => setUserIdPlaceholder("")}
              onBlur={(e) => {
                if (!e.target.value) setUserIdPlaceholder("User ID");
              }}
              InputProps={{
                sx: {
                  textAlign: "center",
                  "&::placeholder": { color: "grey.400", opacity: 1 },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 10,
                  backgroundColor: "transparent",
                  "& fieldset": { borderColor: "grey.500" },
                  "&:hover fieldset": { borderColor: "grey.400" },
                  "&.Mui-focused fieldset": { borderColor: "grey.300" },
                  "& input": {
                    color: "white",
                    textAlign: "center",
                    "&:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px transparent inset",
                      WebkitTextFillColor: "white",
                      caretColor: "white",
                      transition: "background-color 9999s ease-out 0s",
                    },
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="PIN"
              id="PIN"
              type="password"
              autoComplete="current-password"
              placeholder={pinPlaceholder}
              onFocus={() => setPinPlaceholder("")}
              onBlur={(e) => {
                if (!e.target.value) setPinPlaceholder("PIN");
              }}
              InputProps={{
                sx: {
                  textAlign: "center",
                  "&::placeholder": { color: "grey.400", opacity: 1 },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 10,
                  backgroundColor: "transparent",
                  "& fieldset": { borderColor: "grey.500" },
                  "&:hover fieldset": { borderColor: "grey.400" },
                  "&.Mui-focused fieldset": { borderColor: "grey.300" },
                  "& input": {
                    color: "white",
                    textAlign: "center",
                    "&:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 1000px transparent inset",
                      WebkitTextFillColor: "white",
                      caretColor: "white",
                      transition: "background-color 9999s ease-out 0s",
                    },
                  },
                },
              }}
            />

            {/* Image-based sign-in button */}
            <Box
              mt={-1}
              width="100%"
              display="flex"
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
              position="relative"
            >
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "transparent",
                  color: "white",
                  border: "none",
                  boxShadow: "none",
                  textTransform: "none",
                  fontSize: "18px",
                  "&:hover": { backgroundColor: "transparent", boxShadow: "none" },
                  position: "absolute",
                  width: "100%",
                  height: "60px",
                  zIndex: 2,
                  padding: 0,
                }}
              >
                Sign In
              </Button>

              <Box
                component="img"
                src={btnBackground}
                alt="Sign In Button"
                sx={{
                  width: "118%",
                  height: "120px",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          <Copyright sx={{ mt: 4 }} />
        </Paper>

        {/* Bottom wave image */}
        <Box
          component="img"
          src={bottomImage}
          alt="Bottom Illustration"
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            height: "auto",
            objectFit: "contain",
          }}
        />
      </Box>
    </ThemeProvider>
  );
}
